/**
 * usePwaInstall.js
 *
 * The Android / desktop install card (`@khmyznikov/pwa-install`).
 *
 * Rules
 * ─────
 *  1. Only in a true PWA environment (browser, NOT already-installed standalone).
 *  2. Apple devices (iOS/iPadOS) are EXCLUDED — IosInstallCoach.vue handles those
 *     with the app's Arabic i18n and the F7 sheet design language (on iPhone
 *     Safari the scheduler shows the coach in this card's slot).
 *     (@khmyznikov/pwa-install ≥ 0.6.3 added iOS support, so without this gate
 *     both dialogs would fire simultaneously on Apple devices.)
 *  3. The card never opens on its own. `requestInstall(reason)` queues it in the
 *     nudge scheduler (useEngagementNudges.js), which shows it only to a
 *     signed-in user, on the club's own trigger, on Android only after
 *     Chrome's beforeinstallprompt, with no other overlay open.
 *     The triggers: sellers after a push nudge is answered, and at the first
 *     boot of a later session once one was (AppLayout, LaunchPage); suppliers
 *     on the first /suppliers/ tab load after login (AppLayout); collectors at
 *     day open (CollectorDayPage).
 *  4. Respects the user's "dismiss" choice — the scheduler skips the card for
 *     PWA_SNOOZE_DAYS days (useEngagementNudges.js; stored in localStorage so it
 *     survives refreshes).
 *  5. `promptInstall()` is for a direct tap: a tap is a gesture and never queues.
 *  6. On the collector's day (/collector/…) only through its own trigger: the
 *     day page, once the morning download is on the phone, Android Chrome,
 *     once per plan (CollectorDayPage, reason 'day-open'). Never on a shop
 *     page: the card slides up from the bottom, exactly over «خلصت المحل» and
 *     each sheet's own action, and it has no close button — only a drag handle.
 *  7. Samsung Internet, Mi, Oppo / vivo, Phoenix, Opera, Firefox and UC on
 *     Android (capabilities.isAndroidNonChrome) install nothing real — Samsung's
 *     WebAPK has been blocked by Play Protect since April 2026, the others make
 *     shortcuts — so the card's own "how to install here" instructions are never
 *     shown there. A signed-in member gets the ONE-button sheet «ثبّت من كروم»
 *     (InstallInChromeSheet.vue, mounted in App.vue) instead: a launch token
 *     minted on its open, opened in Chrome through the intent URL whose
 *     fallback is the same /launch?t= link. Same slot, same scheduler, same
 *     snooze: it is this composable's second presenter, not a second path.
 *     A visitor who is not signed in keeps the card (the issue endpoint needs
 *     a session).
 *
 * The <pwa-install> web component must be present in the DOM (we add it
 * lazily). Its `showDialog()` method is called programmatically — we NEVER rely
 * on the browser's automatic prompt.
 */

import { ref } from 'vue';
import {
  enqueueNudge,
  markInstallAvailable,
  PWA_SNOOZE_DAYS,
} from '@/composables/useEngagementNudges.js';
import { isAndroidNonChrome, isAppleDevice, isStandalone } from '@/platform/capabilities.js';
import { useAuthStore } from '@/stores/authStore';

// ── Config constants (adjust freely) ──────────────────────────────────────────
const STORAGE_KEY       = 'pwa_install_snoozed_until'; // read by useEngagementNudges
// The card's close: 0.6.3 reports the outcome of its own dialog and of the
// native prompt as one event; it has no hide event.
const CLOSE_EVENTS      = ['pwa-user-choice-result-event', 'pwa-install-success-event', 'pwa-install-fail-event'];
// ──────────────────────────────────────────────────────────────────────────────

const isInstallable = ref(false);   // can the app be installed right now?
let   _element      = null;         // cached reference to <pwa-install> element

// The "install in Chrome" sheet (rule 7): App.vue binds `chromeHandoffOpen` to
// InstallInChromeSheet's `opened` and reports its close through
// `chromeHandoffClosed(outcome)`.
const chromeHandoffOpen = ref(false);
let   _chromeHandoffResolve = null;
// Set by the sheet itself on mount: with no sheet in the tree an ask resolves
// 'unshown' at once instead of holding the scheduler's queue forever.
let   _chromeHandoffMounted = false;

/**
 * True when running in a non-Apple browser context where @khmyznikov/pwa-install
 * should handle the install prompt.
 *
 * Apple devices (iPhone/iPad/iPod and iPadOS-as-MacIntel) are excluded because:
 *  - @khmyznikov/pwa-install ≥ 0.6.3 added iOS support, so without this gate
 *    it would show its iOS dialog AND IosInstallCoach.vue simultaneously.
 *  - IosInstallCoach.vue is the designated handler for all Apple devices —
 *    it uses the app's Arabic i18n and the F7 sheet design language.
 */
function isBrowserContext() {
  if (typeof window === 'undefined') return false;
  // Already installed as standalone PWA — no point prompting again.
  if (isStandalone()) return false;

  // Apple devices are handled by IosInstallCoach.vue — exclude them here.
  return !isAppleDevice();
}

// A signed-in member: the launch-token issue endpoint needs a Sanctum session.
// Read defensively — the store is not active in every unit test that imports
// this module.
function signedIn() {
  try {
    return useAuthStore().isAuthenticated === true;
  } catch {
    return false;
  }
}

/**
 * Rule 7: in an Android browser other than Chrome, a signed-in member's
 * install is the handoff to Chrome, not the card.
 */
function viaChromeHandoff() {
  return isBrowserContext() && isAndroidNonChrome() && signedIn();
}

/** InstallInChromeSheet reports whether it is in the tree (mount / unmount). */
function setChromeHandoffMounted(mounted) {
  _chromeHandoffMounted = mounted === true;
  // Unmounted while an ask waits: nothing will ever report a close.
  if (!_chromeHandoffMounted && _chromeHandoffResolve) chromeHandoffClosed('unshown');
}

/** Open the "install in Chrome" sheet; resolves when App.vue reports its close. */
function presentChromeHandoff() {
  if (!_chromeHandoffMounted) return Promise.resolve('unshown');
  if (_chromeHandoffResolve) {
    // Already open: the second ask joins the first one's close.
    return new Promise((resolve) => {
      const first = _chromeHandoffResolve;
      _chromeHandoffResolve = (outcome) => {
        first(outcome);
        resolve(outcome);
      };
    });
  }
  chromeHandoffOpen.value = true;
  return new Promise((resolve) => {
    _chromeHandoffResolve = resolve;
  });
}

/**
 * App.vue: the sheet closed. 'handoff' (Chrome opened — the install goes on
 * there, and this browser can never see it land) and 'dismissed' («بعدين»,
 * the backdrop) snooze for PWA_SNOOZE_DAYS, like the card's dismiss.
 * 'unshown' (the link never got ready — a failed mint, a sign-out, the sheet
 * taken back) is no decision of his: no snooze, and the scheduler takes a
 * once-key back. Safe with no sheet open.
 *
 * @param {'handoff'|'dismissed'|'unshown'} [outcome]
 */
function chromeHandoffClosed(outcome = 'dismissed') {
  chromeHandoffOpen.value = false;
  if (outcome === 'handoff' || outcome === 'dismissed') snooze();
  const resolve = _chromeHandoffResolve;
  _chromeHandoffResolve = null;
  resolve?.(outcome);
}

/** Record a snooze so we don't re-prompt for PWA_SNOOZE_DAYS */
function snooze() {
  try {
    const until = Date.now() + PWA_SNOOZE_DAYS * 24 * 60 * 60 * 1_000;
    localStorage.setItem(STORAGE_KEY, String(until));
  } catch { /* storage may be blocked */ }
}

/** Lazily create and mount the <pwa-install> element (once) */
function ensureElement() {
  if (_element) return _element;

  _element = document.getElementById('pwa-install-prompt');
  if (_element) return _element;

  _element = document.createElement('pwa-install');
  _element.id             = 'pwa-install-prompt';
  _element.setAttribute('manifest-url', '/manifest.webmanifest');
  // Suppress the package's own Apple auto-detection: we call showDialog()
  // explicitly when the scheduler says so, and IosInstallCoach handles Apple.
  _element.setAttribute('manual-apple', '');

  // The user dismissed the card (its close or a swipe) or the native prompt.
  _element.addEventListener('pwa-user-choice-result-event', (event) => {
    isInstallable.value = false;
    if (event.detail?.message === 'dismissed') snooze();
    // Chrome's install event is single-use: once accepted, the card has no
    // working install button until the next page load brings a new one.
    if (event.detail?.message === 'accepted') {
      window.promptEvent = null;
      markInstallAvailable(false);
    }
  });

  // Listen for successful install.
  _element.addEventListener('pwa-install-success-event', () => {
    isInstallable.value = false;
  });
  // Chrome's install event reached the card: the scheduler may offer it.
  _element.addEventListener('pwa-install-available-event', () => {
    markInstallAvailable();
  });


  _element.addEventListener('pwa-install-fail-event', (event) => {
    console.log('PWA installation failed:', event.detail);
  });

  document.body.appendChild(_element);
  return _element;
}

/**
 * Ask for the install card. Queues it in the nudge scheduler, which decides
 * whether and when it shows (rule 3). Safe to call any number of times; an
 * identical pending request is ignored.
 *
 * @param {'session'|'launch'|'value-moment'|'first-browse'|'day-open'} reason
 * @param {{ onceKey?: string }} [options] a localStorage key that makes this
 *   offer show once: written when the card opens (the collector's, per plan).
 * @returns {boolean} false when the scheduler ignored it.
 */
function requestInstall(reason, { onceKey } = {}) {
  return enqueueNudge(onceKey ? { kind: 'install', reason, onceKey } : { kind: 'install', reason });
}

// How often the presenter checks the card's own hidden flag, the backstop for
// a close that reports no event (a hide the package does by itself).
const HIDDEN_POLL_MS = 1_000;

/**
 * Resolve once the card has closed, whatever the outcome: one of its close
 * events, or its own `isDialogHidden` flag turning true without one. Never
 * left pending while the card is gone — an open nudge holds the scheduler's
 * queue and a reload hold.
 */
function closed(el) {
  return new Promise((resolve) => {
    let poll = null;
    const done = () => {
      CLOSE_EVENTS.forEach((name) => el.removeEventListener(name, done));
      clearInterval(poll);
      resolve();
    };
    CLOSE_EVENTS.forEach((name) => el.addEventListener(name, done));
    poll = setInterval(() => {
      if (el.isDialogHidden === true || !el.isConnected) done();
    }, HIDDEN_POLL_MS);
  });
}

/**
 * Load the web component (kept out of the critical bundle) and hand it
 * Chrome's captured install event. Resolves to the element ready to show, or
 * null when it cannot show here.
 */
async function loadCard() {
  if (!isBrowserContext()) return null;
  try {
    await import('@khmyznikov/pwa-install');
  } catch (err) {
    console.warn('[PWA Install] Failed to load pwa-install component:', err);
    return null;
  }
  const el = ensureElement();
  el.externalPromptEvent = window.promptEvent;
  // Give the browser one tick to upgrade the custom element.
  await new Promise((resolve) => requestAnimationFrame(resolve));
  return typeof el.showDialog === 'function' ? el : null;
}

/**
 * The scheduler's install presenter: open the card and resolve when it closes,
 * or at once with 'unshown' when it cannot open here (the scheduler then takes
 * back a once-per-plan key — the collector was never offered anything).
 */
async function presentInstallCard() {
  if (viaChromeHandoff()) {
    // 'unshown' passes through to the scheduler; the other outcomes were shown.
    const outcome = await presentChromeHandoff();
    return outcome === 'unshown' ? 'unshown' : undefined;
  }
  const el = await loadCard();
  if (!el) return 'unshown';
  const whenClosed = closed(el);
  isInstallable.value = true;
  el.showDialog(true);   // `true` = show even on desktop
  await whenClosed;
}

/**
 * Open the install card directly, from a tap (the profile row, the waiting
 * screen's «ثبّت التطبيق»): a tap is a gesture and never queues.
 */
async function promptInstall() {
  if (viaChromeHandoff()) {
    // Not awaited: a tap opens the sheet; nothing waits for its close.
    presentChromeHandoff();
    return;
  }
  const el = await loadCard();
  if (el) el.showDialog(true);
}

export function usePwaInstall() {
  return {
    /** True when the install prompt is visible / available */
    isInstallable,
    requestInstall,
    presentInstallCard,
    promptInstall,
    /** The "install in Chrome" sheet's open state and its close report (App.vue). */
    chromeHandoffOpen,
    chromeHandoffClosed,
    setChromeHandoffMounted,
  };
}
